"use client";

import {
  RECURRING_STATUS,
  RECURRING_STATUS_NAME,
  formatFrequency,
} from "../services/recurring-order-service";

export interface RecurringOrderRow {
  id: number;
  customer_id: number;
  customer_name?: string;
  branch_id: number;
  branch_name?: string;
  frequency_type: number;
  frequency_value: string;
  next_order_date: string;
  preferred_time: string;
  total_orders_created: number;
  requires_counting?: number;
  status: number;
  created_at: string;
}

interface RecurringOrderTableProps {
  rows: RecurringOrderRow[];
  isLoading: boolean;
  startIndex: number;
  onViewDetail?: (row: RecurringOrderRow) => void;
  onEdit?: (row: RecurringOrderRow) => void;
  onAssignStaff?: (row: RecurringOrderRow) => void;
  onToggleStatus?: (row: RecurringOrderRow) => void;
  onEnd?: (row: RecurringOrderRow) => void;
}

const statusColor: Record<number, string> = {
  [RECURRING_STATUS.ACTIVE]: "bg-green-100 text-green-800",
  [RECURRING_STATUS.PAUSED]: "bg-yellow-100 text-yellow-800",
  [RECURRING_STATUS.ENDED]: "bg-gray-100 text-gray-500",
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "\u2014";
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const RecurringOrderTable: React.FC<RecurringOrderTableProps> = ({
  rows,
  isLoading,
  startIndex,
  onViewDetail,
  onEdit,
  onAssignStaff,
  onToggleStatus,
  onEnd,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <svg className="animate-spin h-6 w-6 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm font-medium">Đang tải dữ liệu...</span>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-12 h-12 mb-3 text-gray-300">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
        </svg>
        <span className="text-sm font-medium">Chưa có đơn định kỳ nào</span>
        <span className="text-xs text-gray-300 mt-1">Tạo đơn định kỳ mới để bắt đầu</span>
      </div>
    );
  }

  return (
    <table className="min-w-full border text-sm">
      <thead className="bg-gray-100">
        <tr>
          <th className="border px-3 py-2 text-center">STT</th>
          <th className="border px-3 py-2 text-left">Khách hàng</th>
          <th className="border px-3 py-2 text-left">Chi nhánh</th>
          <th className="border px-3 py-2 text-left">Tần suất</th>
          <th className="border px-3 py-2 text-center">Giờ tạo</th>
          <th className="border px-3 py-2 text-center">Ngày tạo tiếp</th>
          <th className="border px-3 py-2 text-center">Số đơn đã tạo</th>
          <th className="border px-3 py-2 text-center">Kiểm đếm</th>
          <th className="border px-3 py-2 text-center">Trạng thái</th>
          <th className="border px-3 py-2 text-center">Thao tác</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={row.id} className="hover:bg-gray-50">
            <td className="border px-3 py-2 text-center">{startIndex + idx}</td>
            <td className="border px-3 py-2">
              {row.customer_name || `KH #${row.customer_id}`}
            </td>
            <td className="border px-3 py-2">
              {row.branch_name || `CN #${row.branch_id}`}
            </td>
            <td className="border px-3 py-2">
              {formatFrequency(row.frequency_type, row.frequency_value)}
            </td>
            <td className="border px-3 py-2 text-center">{row.preferred_time}</td>
            <td className="border px-3 py-2 text-center font-semibold">
              {formatDate(row.next_order_date)}
            </td>
            <td className="border px-3 py-2 text-center font-bold text-blue-600">
              {row.total_orders_created}
            </td>
            <td className="border px-3 py-2 text-center">
              {Number(row.requires_counting) === 1 ? (
                <span className="inline-block rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
                  Có
                </span>
              ) : (
                <span className="text-xs text-gray-400">Không</span>
              )}
            </td>
            <td className="border px-3 py-2 text-center">
              <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusColor[row.status] || "bg-gray-100 text-gray-800"}`}>
                {RECURRING_STATUS_NAME[row.status] || "\u2014"}
              </span>
            </td>
            <td className="border px-3 py-2">
              <div className="flex justify-center gap-2">
                {onViewDetail && (
                  <button
                    type="button"
                    onClick={() => onViewDetail(row)}
                    className="group relative grid h-8 w-8 place-items-center rounded-full bg-slate-500 text-white shadow-sm hover:bg-slate-600"
                    title="Xem chi tiết"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </button>
                )}

                {row.status !== RECURRING_STATUS.ENDED && onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(row)}
                    className="group relative grid h-8 w-8 place-items-center rounded-full bg-blue-500 text-white shadow-sm hover:bg-blue-600"
                    title="Sửa"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zM19.5 12.75v6a2.25 2.25 0 01-2.25 2.25h-12a2.25 2.25 0 01-2.25-2.25v-12A2.25 2.25 0 015.25 4.5h6"/>
                    </svg>
                  </button>
                )}

                {row.status !== RECURRING_STATUS.ENDED && onAssignStaff && (
                  <button
                    type="button"
                    onClick={() => onAssignStaff(row)}
                    className="group relative grid h-8 w-8 place-items-center rounded-full bg-teal-500 text-white shadow-sm hover:bg-teal-600"
                    title="Gán nhân sự mặc định"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM3 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 019.374 21c-2.331 0-4.512-.645-6.374-1.766z"/>
                    </svg>
                  </button>
                )}

                {row.status !== RECURRING_STATUS.ENDED && onToggleStatus && (
                  <button
                    type="button"
                    onClick={() => onToggleStatus(row)}
                    className={`group relative grid h-8 w-8 place-items-center rounded-full text-white shadow-sm ${
                      row.status === RECURRING_STATUS.ACTIVE
                        ? "bg-yellow-500 hover:bg-yellow-600"
                        : "bg-green-500 hover:bg-green-600"
                    }`}
                    title={row.status === RECURRING_STATUS.ACTIVE ? "Tạm dừng" : "Kích hoạt"}
                  >
                    {row.status === RECURRING_STATUS.ACTIVE ? (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5"/>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z"/>
                      </svg>
                    )}
                  </button>
                )}

                {row.status !== RECURRING_STATUS.ENDED && onEnd && (
                  <button
                    type="button"
                    onClick={() => onEnd(row)}
                    className="group relative grid h-8 w-8 place-items-center rounded-full bg-red-500 text-white shadow-sm hover:bg-red-600"
                    title="Kết thúc"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 7.5A2.25 2.25 0 017.5 5.25h9a2.25 2.25 0 012.25 2.25v9a2.25 2.25 0 01-2.25 2.25h-9a2.25 2.25 0 01-2.25-2.25v-9z"/>
                    </svg>
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default RecurringOrderTable;
